import { useId, useRef, useState } from 'react';
import {
  Button,
  Box,
  Checkbox,
  MultiSelect,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useCallableContext } from '@/hooks/api/automations/useExecutionCompositions.ts';
import type {
  ExactVersion,
  Location,
  PrepareRequest,
  ProofKind,
  Requirement,
} from '@/types/executionCompositions.ts';
import { locationKey, locationLabel, proofKindLabel } from './compositionPresentation.ts';

export default function RequirementEditor({
  targets,
  existingRequirements,
  onAdd,
  disabled,
}: {
  targets: { location: Location; implementation: ExactVersion }[];
  existingRequirements: PrepareRequest['requirements'];
  onAdd: (requirement: PrepareRequest['requirements'][number]) => boolean | void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const formId = useId();
  const disclosure = useRef<HTMLButtonElement>(null);
  const [targetKey, setTargetKey] = useState<string | null>(null);
  const target = targets.find((item) => locationKey(item.location) === targetKey);
  const context = useCallableContext(target?.implementation.automation_version_id);
  const [caseId, setCaseId] = useState<string | null>(null);
  const [kind, setKind] = useState<ProofKind>('parent_route');
  const [id, setId] = useState('');
  const [scope, setScope] = useState('');
  const [required, setRequired] = useState(true);
  const [outcome, setOutcome] = useState<Requirement['outcome']>('non_artifact');
  const [stateAssertion, setStateAssertion] = useState<string | null>(null);
  const [identityAssertion, setIdentityAssertion] = useState<string | null>(null);
  const [content, setContent] = useState<string[]>([]);
  const [baseline, setBaseline] = useState('');
  const [hash, setHash] = useState('');
  const [subject, setSubject] = useState('');
  const [resultAssertion, setResultAssertion] = useState<string | null>(null);
  const [actualPort, setActualPort] = useState<string | null>(null);
  const [baselinePort, setBaselinePort] = useState<string | null>(null);
  const [expected, setExpected] = useState<
    'matched' | 'mismatch' | 'manual_review' | 'not_comparable'
  >('matched');
  const actualRequirement = existingRequirements.find(
    (item) => item.declaration.requirement_id === subject,
  );
  const actualContext = useCallableContext(
    actualRequirement?.declaration.evidence.automation_version_id,
  );
  const verifiedContext =
    context.isFetchedAfterMount && !context.isError ? context.data : undefined;
  const verifiedActualContext =
    actualContext.isFetchedAfterMount && !actualContext.isError ? actualContext.data : undefined;
  const selectedCase = verifiedContext?.test_cases.find((item) => item.case_id === caseId);
  const options =
    selectedCase?.assertions.map((item) => ({
      value: item.id,
      label: `${item.id} · ${item.type ?? item.kind ?? item.assertion_type ?? 'assertion'}`,
    })) ?? [];
  const baselinePortOptions =
    verifiedContext?.triggers.flatMap(
      (trigger) =>
        trigger.interface?.inputs.map((port) => ({ value: port.port_id, label: port.port_id })) ??
        [],
    ) ?? [];
  const actualPortOptions =
    verifiedActualContext?.triggers.flatMap(
      (trigger) =>
        trigger.interface?.inputs.map((port) => ({ value: port.port_id, label: port.port_id })) ??
        [],
    ) ?? [];
  const actualRequirementOptions = existingRequirements
    .filter(
      (item) =>
        item.declaration.kind !== 'comparison' && item.declaration.outcome === 'produced',
    )
    .map((item) => ({
      value: item.declaration.requirement_id,
      label: item.declaration.scope || item.declaration.requirement_id,
    }));
  const clearCase = () => {
    setCaseId(null);
    setStateAssertion(null);
    setIdentityAssertion(null);
    setContent([]);
    setResultAssertion(null);
    setActualPort(null);
    setBaselinePort(null);
  };
  const add = () => {
    if (!target || !selectedCase?.evidence) return;
    const declaration: Requirement = {
      requirement_id: id,
      scope,
      entrypoint: {
        automation_version_id: target.location.root.automation_version_id,
        trigger_step_id: target.location.root.trigger_step_id,
      },
      kind,
      outcome,
      required_for_publication: required,
      evidence: selectedCase.evidence,
      output_state_assertion_id: stateAssertion,
      artifact_identity_assertion_id: outcome === 'produced' ? identityAssertion : null,
      content_assertion_ids: outcome === 'unavailable' ? [] : content,
      ...(kind === 'comparison'
        ? {
            comparison_baseline: {
              evidence_ref: baseline,
              content_sha256: hash,
              authority_scope: 'comparison_baseline' as const,
            },
            comparison: {
              actual_requirement_id: subject,
              expected_result: expected,
              result_assertion_id: resultAssertion!,
              input_binding: { actual_trigger_id: actualPort!, baseline_trigger_id: baselinePort! },
            },
          }
        : {}),
    };
    if (onAdd({ declaration, evidence_location: target.location }) === false) return;
    setId('');
    setScope('');
    setOpen(false);
    disclosure.current?.focus();
  };
  const complete = Boolean(
    id.trim() &&
    scope.trim() &&
    selectedCase?.evidence &&
    (outcome === 'non_artifact' || stateAssertion) &&
    (kind !== 'final_artifact' ||
      outcome === 'unavailable' ||
      (outcome === 'produced' && identityAssertion && content.length)) &&
    (kind !== 'comparison' ||
      (stateAssertion &&
        baseline &&
        /^[a-f0-9]{64}$/.test(hash) &&
        subject &&
        actualRequirement &&
        resultAssertion &&
        content.includes(resultAssertion) &&
        actualPort &&
        baselinePort &&
        actualPort !== baselinePort &&
        (outcome !== 'produced' || (identityAssertion && content.length)))),
  );
  return (
    <Stack gap="xs">
      <Button
        ref={disclosure}
        variant="default"
        aria-expanded={open}
        aria-controls={formId}
        onClick={() => setOpen((value) => !value)}
      >
        {open ? 'Close requirement form' : 'Add requirement'}
      </Button>
      <Box display={open ? undefined : 'none'} id={formId}>
        <Stack gap="xs">
          <Text fw={600}>Declare a requirement</Text>
          <Text size="sm">
            Choose a saved test case and the checks that prove this requirement. Adding a
            requirement does not run the test or record customer acceptance.
          </Text>
          <Select
            label="Evidence location"
            data={targets.map((item) => ({
              value: locationKey(item.location),
              label: locationLabel(item.location),
            }))}
            value={targetKey}
            onChange={(value) => {
              setTargetKey(value);
              clearCase();
              setKind(
                targets.find((item) => locationKey(item.location) === value)?.location.call_chain
                  .length
                  ? 'component'
                  : 'parent_route',
              );
            }}
            disabled={disabled || !targets.length}
            description={
              !targets.length
                ? 'Choose versions and execution triggers before adding a requirement.'
                : undefined
            }
          />
          {context.isError && (
            <Stack gap="xs">
              <Text c="red">Exact evidence definitions unavailable.</Text>
              <Button
                variant="subtle"
                disabled={disabled || context.isFetching}
                onClick={() => void context.refetch()}
              >
                Retry test-case discovery
              </Button>
            </Stack>
          )}
          <Select
            label="Saved test case"
            description={
              !target
                ? 'Select an evidence location first.'
                : context.isError
                  ? 'Test definitions are unavailable for this exact version.'
                  : !verifiedContext
                    ? 'Loading saved test cases…'
                    : !verifiedContext.test_cases.length
                      ? 'No saved test cases exist for this exact version.'
                      : 'Cases without qualifying checks cannot provide requirement evidence.'
            }
            data={
              verifiedContext?.test_cases.map((item) => ({
                value: item.case_id,
                label: item.evidence ? item.name : `${item.name} · no qualifying assertions`,
                disabled: !item.evidence,
              })) ?? []
            }
            value={caseId}
            onChange={(value) => {
              clearCase();
              setCaseId(value);
            }}
            disabled={disabled || !verifiedContext}
          />
          <Select
            label="What this test proves"
            value={kind}
            onChange={(value) => {
              setKind(value as ProofKind);
              setOutcome('non_artifact');
            }}
            data={
              target?.location.call_chain.length
                ? [{ value: 'component', label: proofKindLabel.component }]
                : [
                    { value: 'component', label: proofKindLabel.component },
                    { value: 'parent_route', label: proofKindLabel.parent_route },
                    { value: 'final_artifact', label: proofKindLabel.final_artifact },
                    { value: 'comparison', label: proofKindLabel.comparison },
                  ]
            }
            description={
              kind === 'component'
                ? 'A component check cannot prove the complete workflow or final output.'
                : kind === 'parent_route'
                  ? 'Checks the complete route from this entrypoint.'
                  : kind === 'final_artifact'
                    ? 'Checks whether the final output exists and meets its declared requirements.'
                    : 'Checks an actual result against an eligible baseline.'
            }
            disabled={disabled}
          />
          <TextInput
            label="Requirement ID"
            value={id}
            onChange={(event) => setId(event.currentTarget.value)}
            disabled={disabled}
          />
          <TextInput
            label="Deliverable description"
            value={scope}
            onChange={(event) => setScope(event.currentTarget.value)}
            disabled={disabled}
          />
          <Checkbox
            label="Track this test for the release (it does not block publishing)."
            checked={required}
            onChange={(event) => setRequired(event.currentTarget.checked)}
            disabled={disabled}
          />
          <Select
            label="Expected output"
            value={outcome}
            onChange={(value) => setOutcome(value as Requirement['outcome'])}
            data={
              kind === 'comparison'
                ? [
                    { value: 'produced', label: 'Artifact comparison report' },
                    { value: 'non_artifact', label: 'Structured JSON comparison report' },
                  ]
                : [
                    { value: 'non_artifact', label: 'Structured result' },
                    { value: 'produced', label: 'Produced artifact' },
                    { value: 'unavailable', label: 'No output expected' },
                  ]
            }
            disabled={disabled}
          />
          <Select
            label="Output-state assertion"
            data={options}
            value={stateAssertion}
            onChange={setStateAssertion}
            clearable
            disabled={disabled}
          />
          {outcome === 'produced' && (
            <Select
              label="Artifact-identity assertion"
              data={options}
              value={identityAssertion}
              onChange={setIdentityAssertion}
              disabled={disabled}
            />
          )}
          {outcome !== 'unavailable' && (
            <MultiSelect
              label="Content assertions"
              data={options}
              value={content}
              onChange={setContent}
              disabled={disabled}
            />
          )}
          {kind === 'comparison' && (
            <>
              <Select
                label="Actual artifact to compare"
                value={subject}
                onChange={(value) => {
                  setSubject(value ?? '');
                  setActualPort(null);
                }}
                data={actualRequirementOptions}
                description={
                  actualRequirementOptions.length
                    ? 'Select a previously declared produced artifact. Its compatible inputs are shown below.'
                    : 'Add a produced-artifact requirement before adding a comparison.'
                }
                disabled={disabled || !actualRequirementOptions.length}
              />
              <TextInput
                label="Expected baseline reference"
                value={baseline}
                onChange={(event) => setBaseline(event.currentTarget.value)}
                disabled={disabled}
                description="Reference does not establish approval; the server verifies authority."
              />
              <TextInput
                label="Baseline content SHA-256"
                value={hash}
                onChange={(event) => setHash(event.currentTarget.value)}
                disabled={disabled}
              />
              <Select
                label="Expected comparison result"
                value={expected}
                onChange={(value) => setExpected(value as typeof expected)}
                data={['matched', 'mismatch', 'manual_review', 'not_comparable']}
                disabled={disabled}
              />
              <Select
                label="Result assertion"
                value={resultAssertion}
                onChange={setResultAssertion}
                data={options}
                disabled={disabled}
              />
              <Select
                label="Actual input port"
                value={actualPort}
                onChange={setActualPort}
                data={actualPortOptions}
                disabled={disabled || !actualRequirement || !verifiedActualContext}
              />
              <Select
                label="Baseline input port"
                value={baselinePort}
                onChange={setBaselinePort}
                data={baselinePortOptions}
                disabled={disabled}
              />
            </>
          )}
          <Text size="sm" id={`${formId}-reason`}>
            {disabled
              ? 'Requirement changes are unavailable while this candidate is read-only or an action is pending.'
              : !complete
                ? 'Choose a qualifying case, enter an ID and description, and select the checks required for this result.'
                : 'This declaration will be included in your unsaved proposal.'}
          </Text>
          <Button
            aria-describedby={`${formId}-reason`}
            disabled={disabled || !complete}
            onClick={add}
          >
            Include requirement
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
